/** @format */
import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton, Divider, Tooltip, Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import MuiAppBar from "@mui/material/AppBar";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Viewer, Worker, ScrollMode, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import './mergePreview.css'
import ProfileMenu from "../Menubar/ProfileMenu";
import { esignIcons } from "./icons";
import { uploadDocument } from "../../store/reducers/uploadDocumentSlice";
import workerSrc from "pdfjs-dist/build/pdf.worker?url";
import { esignToolApi } from "../../services/esignApi";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;


const AppBar = styled(MuiAppBar)(() => ({
  background: "#fff",
  color: "#000",
  boxShadow: "rgba(0,0,0,0.05) 0px 0px 0px 1px",
}));

const MergePdfs = ({ selectedFiles, setSelectedFiles, setSelectedPdf, setLoader, loader, BulkEsign=false }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pdfEditortoken } = useSelector((state) => state.pdfEditortoken);
  const [fileMeta, setFileMeta] = useState({});


//   const handleAddMore = () => fileInputRef.current?.click();

  // Update preview when selected file changes
//   useEffect(() => {
//     if (!previewFile) return;
//     const url = URL.createObjectURL(previewFile);
//     setPreviewUrl(url);
//     return () => URL.revokeObjectURL(url);
//   }, [previewFile]);

    const getPdfPageCount = async (file) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            return pdf.numPages;
        } catch (e) {
            console.error("Error reading page count:", e);
            return 0;
        }
    };

    useEffect(() => {
        const updateFilesWithPageCount = async () => {
            const newMeta = { ...fileMeta };

            for (const item of selectedFiles) {
           if (item.type !== "pdf" && item.type !== "stamp") continue;

            const name = item.file.name;
            if (newMeta[name]) continue;

            try {
                const pageCount = await getPdfPageCount(item.file);
                newMeta[name] = { pageCount };
            } catch {
                newMeta[name] = { pageCount: 0 };
            }
            }

            setFileMeta(newMeta);
        };

        if (selectedFiles.length) updateFilesWithPageCount();
    }, [selectedFiles]);



    useEffect(() => {
        if (!selectedFiles.length) return;

        const mergePdfs = async () => {
            setLoader(true);
            try {
                const mergedPdf = await PDFDocument.create();

                const A4_WIDTH = 595.28;
                const A4_HEIGHT = 841.89;

                for (const item of selectedFiles) {
                    if (item.type !== "pdf" && item.type !== "stamp") continue;

                    const arrayBuffer = await item.file.arrayBuffer();
                    const pdf = await PDFDocument.load(arrayBuffer);

                    const pageCount = pdf.getPageCount();

                    for (let i = 0; i < pageCount; i++) {
                        const embeddedPage = await mergedPdf.embedPage(
                            pdf.getPage(i)
                        );

                        const { width, height } = embeddedPage.size();

                        const newPage = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);

                        const scale = Math.min(
                            A4_WIDTH / width,
                            A4_HEIGHT / height
                        );

                        const scaledWidth = width * scale;
                        const scaledHeight = height * scale;

                        const x = (A4_WIDTH - scaledWidth) / 2;
                        const y = (A4_HEIGHT - scaledHeight) / 2;

                        newPage.drawPage(embeddedPage, {
                            x,
                            y,
                            width: scaledWidth,
                            height: scaledHeight,
                        });
                    }
                }

                const mergedBytes = await mergedPdf.save();
                const blob = new Blob([mergedBytes], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);

                setLoader(true); // Show loader while PDF is loading in viewer
                setPreviewUrl((oldUrl) => {
                    if (oldUrl) URL.revokeObjectURL(oldUrl);
                    return url;
                });

            } catch (err) {
                console.error("PDF merge error:", err);
                setLoader(false);
            }
        };

        mergePdfs();
    }, [selectedFiles]);


  
    const handleAddMore = () => fileInputRef.current?.click();

    const handleFileSelect = (e) => {
        const newFiles = Array.from(e.target.files).map((file) => ({
            type: "pdf",
            file,
        }));

        setSelectedFiles((prev) => [...prev, ...newFiles]);
        e.target.value = null;
    };


    const handleDelete = (item, index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

        if (item.type === "pdf") {
            setFileMeta((prev) => {
            const meta = { ...prev };
            delete meta[item.file.name];
            return meta;
            });
        }
    };


    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(selectedFiles);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);
        setSelectedFiles(items);
    };

    const thumbnailPluginInstance = thumbnailPlugin({
        renderThumbnailItem: (props) => {
            return (
                <div onClick={props.onJumpToPage}>
                    {props.renderPageThumbnail()}
                </div>
            );
        },
    });

    const handleNext = async () => {
        if (!selectedFiles.length) {
            toast.error("Please select at least one PDF");
            return;
        }

        if (!pdfEditortoken?.access) {
            toast.error("Missing access token");
            return;
        }

        setLoader(true);

        try {
            const stamp = selectedFiles.find((i) => i.type === "stamp");
            const result = await dispatch(
                uploadDocument({
                    token: pdfEditortoken.access,
                    files: selectedFiles,
                    bulkEsign: BulkEsign,
                    stampId: stamp?.stampId || null,
                })
            ).unwrap();

            console.log(result)
            const documentId = result?.data?.document_id;
            if (stamp?.stampId && documentId) {
                await esignToolApi.assignStampToDocument({
                    identity: documentId,
                    Smtampid: stamp.stampId,
                });
            }
            
            setSelectedPdf(false);
        } catch (error) {
            console.error("Upload error:", error);
            toast.error(error);
        } finally {
            setLoader(false);
        }
    };
    
    const fetchStampPaperPdf = async () => {
        const response = await esignToolApi.getStampPaper();

        if (!response?.status || !response?.stamps?.file) {
            console.log(response)
            throw new Error(response?.message || response?.msg || "Failed to add stamp paper");
        }

        const pdfUrl = response.stamps.file;
        const stampId = response.stamps.id;

        const fileResponse = await fetch(pdfUrl);
        const blob = await fileResponse.blob();

        return {
            file: new File([blob], "Stamp_Paper.pdf", {
                type: "application/pdf",
            }),
            stampId,
        };
    };

    const handleStampToggle = async (checked) => {
        const hasStamp = selectedFiles.some((i) => i.type === "stamp");

        if (checked && !hasStamp) {
            try {
                setLoader(true);

                const { file, stampId } = await fetchStampPaperPdf();

                setSelectedFiles((prev) => [
                    ...prev,
                    {
                        type: "stamp",
                        file,
                        stampId, // ✅ STORED HERE
                    },
                ]);
            } catch (err) {
                console.log(err);
                toast.error(err.message);
            } finally {
                setLoader(false);
            }
        }

        if (!checked && hasStamp) {
            const stamp = selectedFiles.find((i) => i.type === "stamp");
            if (stamp?.stampId) {
                try {
                    await esignToolApi.updateStampStatus({
                        Smtampid: stamp.stampId.toString(),
                        status: "UNUSED",
                    });
                } catch (err) {
                    console.error("Error updating stamp status:", err);
                }
            }
            setSelectedFiles((prev) => prev.filter((i) => i.type !== "stamp"));
        }
    };


    const firstPdf = selectedFiles.find((i) => i.type === "pdf");

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: "100%",
        height: "100vh",
        display: "flex",
        background: "#f4f4f4",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 9999,
      }}
    >
      {/* App Bar */}
      <AppBar position="fixed">
        <Box sx={{ height: '80px', display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2em" }}>
            <Box sx={{ display: "flex", cursor: "pointer" }} onClick={() => setSelectedPdf(false)} >
                <svg width="11" height="20" viewBox="0 0 11 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L2 10L9 2" stroke="#2556AB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <Tooltip title={firstPdf?.file?.name}>
                    <Typography noWrap component="p" sx={{ fontSize: '20px', fontWeight: 600, lineHeight: '20px', color: "#1D1D1D", ml: 2 }}>
                        {firstPdf?.file?.name?.length > 20 ? firstPdf?.file?.name.slice(0, 20) + "..." : firstPdf?.file?.name}
                    </Typography>
                </Tooltip>
            </Box>
          <Box>
            <ProfileMenu />
          </Box>
        </Box>
      </AppBar>

      {/* Main Body */}
        <Box sx={{ width: "96%", height: "calc(100vh - 106px)", m: "auto", mt: "94px", borderRadius: "12px", background: "#fff", alignItems: "center" }}>
            <Box sx={{ display: "flex", width: "100%", height: "calc(100% - 84px)",}}>
                {/* Left Panel */}
                <Box sx={{ flex: 1.5, display: "flex" }}>
                    {/* Thumbnails */}
                    <Box sx={{ width: 200, overflowY: "auto", borderRight: "1px solid #eee", p: 1 }}>
                        {previewUrl && (
                        <thumbnailPluginInstance.Thumbnails />
                        )}
                    </Box>

                    {/* PDF Viewer */}
                    <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                        {previewUrl ? (
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer
                                fileUrl={previewUrl}
                                defaultScale={SpecialZoomLevel.PageWidth}
                                scrollMode={ScrollMode.Vertical}
                                plugins={[thumbnailPluginInstance]}
                                onDocumentLoad={() => setLoader(false)}
                            />
                        </Worker>
                        ) : (
                        <Typography sx={{ mt: 10, color: "#777" }}>No file selected</Typography>
                        )}
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ borderWidth: "0 0 0 thin !important", margin: 0 }} />
                    {/* Right Panel */}
                    <Box sx={{ flex: 0.5, background: "#fff", borderRadius: "12px", maxWidth: "450px" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" padding="13px 20px">
                            <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Documents</Typography>
                            <Button variant="contained" sx={{ width: '160px', height: 46, color: "#fff", background: "#185CB7", textTransform: "none", fontSize: "16px", borderRadius: "10px", boxShadow: "none", fontWeight: "500", "&:hover": { background: "#185CB7" }, }} onClick={handleAddMore} >
                                {esignIcons.addMore} Add More
                            </Button>
                        </Box>
                        <Divider />
                        {!BulkEsign && (
                        <Box sx={{ "background": "linear-gradient(93.93deg, rgba(24, 92, 183, 0.06) 30.05%, rgba(205, 48, 77, 0.06) 95.93%)", height: "42px", padding: "0 20px", display: "flex", alignItems: "center", gap: "15px", fontSize: "16px", fontWeight: "500", color: "#141B34" }}>
                            <Checkbox
                                sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 20 } }}
                                // checked={selectedSigners.length === signers.length}
                                // indeterminate={selectedSigners.length > 0 && selectedSigners.length < signers.length}
                                onChange={(e) => handleStampToggle(e.target.checked)}
                            /> Add Stamp Paper
                        </Box>
                        )}
                        {/* Draggable list */}
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="pdf-list">
                            {(provided) => (
                                <Box ref={provided.innerRef} {...provided.droppableProps} padding="30px 20px 0 20px" sx={{ height: "calc(100% - 73px)", overflow: "auto", overflowX: "hidden" }}>
                                {selectedFiles.map((file, i) => (
                                    
                                    <Draggable
                                    key={file.type === "stamp" ? "stamp-paper" : file.file.name}
                                    draggableId={file.type === "stamp" ? "stamp-paper" : file.file.name}
                                    index={i}
                                    isDragDisabled={file.type === "pdf" && i === 0}
                                    >
                                    {(providedDrag) => (
                                        <Box
                                        ref={providedDrag.innerRef}
                                        {...providedDrag.draggableProps}
                                        {...providedDrag.dragHandleProps}
                                        sx={{
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            p: "0 0 0 15px",
                                            mb: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            cursor: "pointer",
                                            backgroundColor: i === 0 ? "#f9fafb" : "#fff",
                                            minWidth: "410px",
                                            height: "64px",
                                        }}
                                        //   onClick={() => setPreviewFile(file)}
                                        >
                                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
                                            <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "14px"  }}>
                                                <Box sx={{ display: "flex" }}>
                                                    <svg width="11" height="25" viewBox="0 0 11 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="2.2" cy="22.0002" r="2.2" transform="rotate(-90 2.2 22.0002)" fill="#333333"/>
                                                    <circle cx="2.2" cy="15.4001" r="2.2" transform="rotate(-90 2.2 15.4001)" fill="#333333"/>
                                                    <circle cx="2.2" cy="8.8" r="2.2" transform="rotate(-90 2.2 8.8)" fill="#333333"/>
                                                    <circle cx="2.2" cy="2.1999" r="2.2" transform="rotate(-90 2.2 2.1999)" fill="#333333"/>
                                                    <circle cx="8.8001" cy="22.0002" r="2.2" transform="rotate(-90 8.8001 22.0002)" fill="#333333"/>
                                                    <circle cx="8.8001" cy="15.4001" r="2.2" transform="rotate(-90 8.8001 15.4001)" fill="#333333"/>
                                                    <circle cx="8.8001" cy="8.8" r="2.2" transform="rotate(-90 8.8001 8.8)" fill="#333333"/>
                                                    <circle cx="8.8001" cy="2.1999" r="2.2" transform="rotate(-90 8.8001 2.1999)" fill="#333333"/>
                                                    </svg>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 16, overflowWrap: "anywhere", color: "#1D1D1D" }}>
                                                        {file.type === "pdf" ? file.file.name?.length > 20 ? file.file.name.slice(0, 20) + "..." : file.file.name : "Stamp Paper"}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 400, color: "#5C5C5C", mt: 0.3, }}>
                                                        {file.type === "pdf" && fileMeta[file.file.name]?.pageCount ? `Pages: ${fileMeta[file.file.name].pageCount}` : ""}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {i !== 0 && file.type === "pdf" && (<>
                                                <Divider orientation="vertical" flexItem sx={{ borderWidth: "0 0 0 thin !important", margin: 0 }} />
                                                <IconButton onClick={(e) => {e.stopPropagation(); handleDelete(file, i);}} sx={{ p: 2 }}>
                                                    {esignIcons.deleteIconRed}
                                                </IconButton>
                                            </>)}
                                        </Box>
                                        </Box>
                                    )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                </Box>
                            )}
                            </Droppable>
                        </DragDropContext>
                    </Box>
                </Box>
            </Box>
            <Divider />
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "end", p: "0 20px"}}>
                <Button
                    variant="contained"
                    sx={{
                        mt: 2.5,
                        width: '172px',
                        height: 46,
                        color: "#fff",
                        background: "#185CB7",
                        textTransform: "none",
                        fontSize: "16px",
                        borderRadius: "10px",
                        boxShadow: "none",
                        fontWeight: "500",
                        "&:hover": { background: "#185CB7" },
                    }}
                    onClick={handleNext}
                    disabled={loader}
                >
                    Next
                </Button>
            </Box>
        </Box>

      {/* Hidden input */}
      <input
        type="file"
        multiple
        accept="application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </Box>
  );
};

export default MergePdfs;
